/**
 * BXGY Auto-Add Controller (Modular Version)
 *
 * Monitors cart changes and automatically adds eligible BXGY variants
 * when trigger conditions are met. Integrates with theme cart events.
 *
 * Key Features:
 * - Priority-based promotion processing
 * - Claims-based quantity tracking to prevent double-redemption
 * - Event-driven cart monitoring
 * - Automatic item addition with batch operations
 */

import {
  ThemeEvents,
  CartAddEvent,
  CartUpdateEvent,
  CartErrorEvent,
} from "@theme/events";
import { ClaimsTracker } from "@theme/bxgy-claims-tracker";
import { PromotionSorter } from "@theme/bxgy-promotion-sorter";
import { PromotionProcessor } from "@theme/bxgy-promotion-processor";
import { CartOperations } from "@theme/bxgy-cart-operations";

class BXGYController {
  constructor() {
    this.configs = null;
    this.debugMode = false;

    // Processing state
    this.isProcessing = false;
    this.isAddingPromoItems = false;
    this.isInitializing = false;
    this.promotionCheckTimeout = null;

    // Cart state tracking
    this.previousCartState = null;
    this.baselineCartState = null;

    // Promotion state tracking
    this.processedPromotions = new Set();
    this.recentlyRemovedItems = new Map();

    // Initialize modules (will be set after config load)
    this.claimsTracker = null;
    this.promotionSorter = null;
    this.promotionProcessor = null;
    this.cartOperations = null;

    this.init();
  }

  async init() {
    try {
      this.log("🚀 Starting BXGY Controller initialization...");

      // Load configurations
      this.loadConfigurations();

      // Initialize modules with debug mode
      this.claimsTracker = new ClaimsTracker(this.debugMode);
      this.promotionSorter = new PromotionSorter(this.debugMode);
      this.promotionProcessor = new PromotionProcessor(
        this.claimsTracker,
        this.debugMode,
      );
      this.cartOperations = new CartOperations(this.debugMode);

      // Set up event listeners
      this.setupEventListeners();

      // Initialize cart state
      await this.initializeCartState();

      this.log("✅ BXGY Controller initialized successfully");
    } catch (error) {
      console.error("[BXGY] ❌ Initialization failed:", error);
    }
  }

  loadConfigurations() {
    const configElement = document.getElementById("bxgy-config-data");
    if (!configElement) {
      this.log("No BXGY configuration found");
      return;
    }

    try {
      this.configs = JSON.parse(configElement.textContent);
      this.debugMode = this.configs.globalSettings?.debugMode || false;

      // Filter out expired promotions on initial load
      this.filterExpiredConfigs();

      this.log("📋 Loaded configurations:", this.configs);
    } catch (error) {
      console.error("Failed to parse BXGY configuration:", error);
    }
  }

  setupEventListeners() {
    document.addEventListener(CartAddEvent.eventName, (event) => {
      this.log("🛒➕ Cart add event:", event.detail);
      this.onCartChange(event.detail);
    });

    document.addEventListener(ThemeEvents.cartUpdate, (event) => {
      this.log("🛒🔄 Cart update event:", event.detail);
      this.onCartChange(event.detail);
    });

    document.addEventListener(ThemeEvents.cartError, (event) => {
      this.log("🛒❌ Cart error event:", event.detail);
      this.onCartError(event.detail);
    });

    this.log("👂 Event listeners registered");
  }

  async initializeCartState() {
    if (!this.configs?.configurations) {
      this.log("No configurations for initial state check");
      return;
    }

    this.isInitializing = true;

    try {
      let cart = await this.cartOperations.getValidatedCartData();
      if (!cart) {
        this.previousCartState = { items: [], total_price: 0 };
        this.baselineCartState = { items: [], total_price: 0 };
        return;
      }

      this.previousCartState = cart;
      this.baselineCartState = cart;

      // Remove any orphaned promo items first (from promotions no longer active)
      const hadOrphans = await this.removeOrphanedPromoItems(cart);

      // Refresh cart after orphan removal
      if (hadOrphans) {
        const refreshedCart = await this.cartOperations.getCurrentCart();
        if (refreshedCart) {
          cart = refreshedCart;
          this.previousCartState = cart;
          this.baselineCartState = cart;
        }
      }

      // Analyze existing promotions in cart
      for (const config of this.configs.configurations) {
        if (this.shouldProcessPromotion(config)) {
          const analysis = this.promotionProcessor.analyzePromotionInCart(
            config,
            cart,
            config.usesPerOrderLimit,
          );

          if (analysis.currentCount > analysis.maxAllowed) {
            const excessItems = analysis.currentCount - analysis.maxAllowed;
            this.log(`Excess items for "${config.name}": ${excessItems}`);
            await this.cartOperations.removeExcessPromotionItems(
              config,
              cart,
              excessItems,
              this.promotionProcessor.isItemFromPromotion.bind(
                this.promotionProcessor,
              ),
            );
          } else if (analysis.isAtLimit) {
            this.processedPromotions.add(config.id);
            this.log(
              `Marked "${config.name}" as processed (${analysis.currentCount}/${analysis.maxAllowed})`,
            );
          }
        }
      }

      await this.checkPromotions(cart);

      this.log("Initial cart state analysis complete");
    } catch (error) {
      console.error("Failed to initialize cart state:", error);
    } finally {
      this.isInitializing = false;
    }
  }

  async onCartChange(eventDetail) {
    if (this.isProcessing) {
      this.log("⏸️ Already processing, skipping");
      return;
    }

    clearTimeout(this.promotionCheckTimeout);
    this.promotionCheckTimeout = setTimeout(async () => {
      let cart = null;

      if (eventDetail?.resource?.items) {
        cart = eventDetail.resource;
      } else {
        cart = await this.cartOperations.getCurrentCart();
      }

      if (cart) {
        // Re-check for newly expired promotions on each cart change
        this.filterExpiredConfigs();

        // Detect removed promo items
        this.detectRemovedPromoItems(cart);

        // Cleanup expired exclusions
        this.cleanupExpiredExclusions();

        // Clear exclusions on new qualifying items
        this.clearExclusionsOnNewQualifyingItems(cart);

        // Store for next comparison
        this.previousCartState = cart;

        // Process promotions with claims-based system
        await this.checkPromotions(cart);

        // Update baseline after processing
        if (!this.isAddingPromoItems) {
          const updatedCart = await this.cartOperations.getCurrentCart();
          if (updatedCart) {
            this.baselineCartState = updatedCart;
          }
        }
      }
    }, 50);
  }

  onCartError(eventDetail) {
    this.log("Cart operation failed:", eventDetail);
    this.isProcessing = false;
  }

  /**
   * Main promotion checking with priority-based claims system
   */
  async checkPromotions(cart) {
    if (
      !this.configs?.configurations ||
      !this.configs.globalSettings?.enableAutoAdd
    ) {
      return;
    }

    this.isProcessing = true;

    try {
      // Remove orphaned promo items first (from promotions no longer active)
      const hadOrphans = await this.removeOrphanedPromoItems(cart);
      if (hadOrphans) {
        // Refresh cart data after orphan removal
        cart = await this.cartOperations.getCurrentCart();
        if (!cart) {
          this.isProcessing = false;
          return;
        }
      }

      // Reset claims for this evaluation cycle
      this.claimsTracker.reset();

      // Get promotions sorted by priority
      const processablePromotions =
        this.promotionSorter.getProcessablePromotions(
          this.configs.configurations,
          {
            processedPromotions: this.processedPromotions,
            recentlyRemovedItems: this.recentlyRemovedItems,
            exclusionWindow: 10000,
          },
        );

      this.log(
        `📊 Processing ${processablePromotions.length} promotions in priority order`,
      );

      const itemsToAdd = [];
      const processedConfigs = [];

      // Process each promotion in priority order
      for (const config of processablePromotions) {
        this.log(
          `\n🎯 Processing: ${config.name} (priority: ${this.promotionSorter.getPriority(config)})`,
        );

        // Check trigger with claims-aware logic
        const triggerResult = this.promotionProcessor.checkTriggerConditions(
          config,
          cart,
        );

        if (
          !triggerResult.triggered &&
          config.customerGets?.rewardStrategy !== "choice"
        ) {
          this.log(
            `❌ ${config.name}: trigger conditions not met (insufficient unclaimed quantities)`,
          );
          continue;
        }

        // Process the promotion
        const processResult = this.promotionProcessor.processPromotion(
          config,
          cart,
          triggerResult,
        );

        // Commit claims if successful - but only for the uses actually applied
        if (processResult.commitClaims && triggerResult.claims) {
          // Calculate how many uses were actually applied (capped by usesPerOrderLimit)
          const actualUses = Math.min(
            triggerResult.qualifiedUses,
            config.usesPerOrderLimit || 1,
          );
          const minQtyRequired = config.customerBuys?.minimumQuantity || 1;
          const qtyToClaimPerUse = minQtyRequired;
          const totalQtyToClaim = actualUses * qtyToClaimPerUse;
          const requirementType =
            config.customerBuys?.requirementType === "minimum_purchase_amount"
              ? "amount"
              : "quantity";

          // Create limited claims - only claim what we actually used
          const limitedClaims = this.limitClaims(
            triggerResult.claims,
            totalQtyToClaim,
          );

          const committed = this.claimsTracker.commitClaims(
            config.id,
            limitedClaims,
            {
              requirementType,
            },
          );
          if (committed) {
            this.log(
              `✅ Committed claims for ${config.name} (${totalQtyToClaim} qty)`,
            );
          }
        }

        // Mark as processed if at limit
        if (processResult.isAtLimit) {
          this.processedPromotions.add(config.id);
        }

        // Collect items to add
        if (processResult.items.length > 0) {
          itemsToAdd.push(...processResult.items);
          processedConfigs.push(config);
        }
      }

      // Log claims summary
      this.log("📋 Claims summary:", this.claimsTracker.getSummary());

      // Check ALL promotions (including already processed) for excess items
      // This handles the case where qualifying qty decreased (e.g., 2 pencils → 1)
      await this.checkAndRemoveExcessItems(cart);

      // Batch add all items
      if (itemsToAdd.length > 0) {
        this.log(
          `🛒 Batch adding ${itemsToAdd.length} items from ${processedConfigs.length} promotions`,
        );
        this.isAddingPromoItems = true;
        await this.cartOperations.batchAddToCart(itemsToAdd, processedConfigs);
        this.isAddingPromoItems = false;
        if (!this.isInitializing) {
          this.openCartDrawer();
        }
      }
    } catch (error) {
      console.error("Error checking promotions:", error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Filter out expired promotion configs and log removals.
   * Mutates this.configs.configurations in place.
   */
  filterExpiredConfigs() {
    if (!this.configs?.configurations) {
      return;
    }

    const now = new Date();
    const before = this.configs.configurations.length;

    this.configs.configurations = this.configs.configurations.filter(
      (config) => {
        if (config.endsAt) {
          const endsAtDate = new Date(config.endsAt);
          const isExpired = endsAtDate <= now;

          if (isExpired) return false;
        } else {
        }
        return true;
      },
    );

    const removed = before - this.configs.configurations.length;
  }

  shouldProcessPromotion(config) {
    if (!config.active || config.status !== "ACTIVE") return false;
    if (this.processedPromotions.has(config.id)) return false;
    if (this.isRecentlyRemoved(config.id)) return false;
    if (config.endsAt && new Date(config.endsAt) <= new Date()) return false;
    return true;
  }

  isRecentlyRemoved(configId) {
    const exclusionWindow = 10000;
    const removal = this.recentlyRemovedItems.get(configId);
    if (!removal) return false;
    return Date.now() - removal.timestamp < exclusionWindow;
  }

  detectRemovedPromoItems(currentCart) {
    if (!this.baselineCartState?.items || !this.configs?.configurations) return;

    for (const config of this.configs.configurations) {
      const baselinePromoItems = this.baselineCartState.items.filter((item) =>
        this.promotionProcessor.isItemFromPromotion(item, config),
      );
      const currentPromoItems = currentCart.items.filter((item) =>
        this.promotionProcessor.isItemFromPromotion(item, config),
      );

      const baselineCount = baselinePromoItems.reduce(
        (sum, item) => sum + item.quantity,
        0,
      );
      const currentCount = currentPromoItems.reduce(
        (sum, item) => sum + item.quantity,
        0,
      );

      if (baselineCount > currentCount) {
        this.log(
          `🗑️ Detected removal: ${baselineCount - currentCount} items from "${config.name}"`,
        );
        this.recentlyRemovedItems.set(config.id, {
          timestamp: Date.now(),
          removedCount: baselineCount - currentCount,
          promotionName: config.name,
        });
      }
    }
  }

  cleanupExpiredExclusions() {
    const exclusionWindow = 5000;
    const now = Date.now();

    for (const [configId, removal] of this.recentlyRemovedItems.entries()) {
      if (now - removal.timestamp >= exclusionWindow) {
        this.log(`Cleared exclusion for "${removal.promotionName}"`);
        this.recentlyRemovedItems.delete(configId);
      }
    }
  }

  clearExclusionsOnNewQualifyingItems(cart) {
    if (!this.previousCartState || !this.configs?.configurations) return;

    for (const config of this.configs.configurations) {
      let prevItems = [];
      let currItems = [];

      if (config.customerBuys.anyItemsFrom === "all_products") {
        prevItems = this.previousCartState.items.filter(
          (item) =>
            !this.promotionProcessor.isPromotionalItem(item) && !item.gift_card,
        );
        currItems = cart.items.filter(
          (item) =>
            !this.promotionProcessor.isPromotionalItem(item) && !item.gift_card,
        );
      } else {
        prevItems = this.promotionProcessor
          .getEligibleCartItems(
            this.previousCartState,
            config.customerBuys.variantIds,
          )
          .filter((item) => !this.promotionProcessor.isPromotionalItem(item));

        currItems = this.promotionProcessor
          .getEligibleCartItems(cart, config.customerBuys.variantIds)
          .filter((item) => !this.promotionProcessor.isPromotionalItem(item));
      }

      const prevCount = prevItems.reduce((sum, item) => sum + item.quantity, 0);
      const currCount = currItems.reduce((sum, item) => sum + item.quantity, 0);

      if (currCount > prevCount) {
        const clearedCount = this.recentlyRemovedItems.size;
        if (clearedCount > 0) {
          this.log(
            `Cleared ${clearedCount} exclusions (new qualifying items added)`,
          );
          this.recentlyRemovedItems.clear();
        }
        break;
      }
    }
  }

  openCartDrawer() {
    const behavior = this.getThemeSetting(
      "bxgy_cart_drawer_behavior",
      "open_on_add",
    );
    if (behavior === "silent") return;

    if (behavior === "notification_only") {
      this.showNotification();
      return;
    }

    try {
      const selector = this.getThemeSetting(
        "bxgy_cart_drawer_selector",
        "cart-drawer-component",
      );
      const cartDrawer = document.querySelector(selector);
      if (cartDrawer && typeof cartDrawer.open === "function") {
        cartDrawer.open();
      } else {
        this.showNotification();
      }
    } catch (error) {
      this.showNotification();
    }
  }

  showNotification() {
    if (!this.getThemeSetting("bxgy_show_notifications", true)) return;

    const message = this.getThemeSetting(
      "bxgy_notification_text",
      "Free item added to your cart!",
    );
    const duration =
      this.getThemeSetting("bxgy_notification_duration", 4) * 1000;

    const notification = document.createElement("div");
    notification.className = "bxgy-notification";
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed; top: 20px; right: 20px;
      background: #007c89; color: white;
      padding: 12px 20px; border-radius: 6px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000; font-size: 14px;
      animation: bxgySlideIn 0.3s ease-out;
    `;

    document.body.appendChild(notification);
    setTimeout(() => {
      notification.style.animation = "bxgySlideOut 0.3s ease-in";
      setTimeout(() => notification.remove(), 300);
    }, duration);
  }

  getThemeSetting(key, defaultValue) {
    if (window.Shopify?.theme?.settings?.[key] !== undefined) {
      return window.Shopify.theme.settings[key];
    }
    if (window.bxgySettings?.[key] !== undefined) {
      return window.bxgySettings[key];
    }
    return defaultValue;
  }

  resetProcessedPromotions() {
    this.processedPromotions.clear();
    this.claimsTracker?.reset();
    this.log("Reset all processed promotions and claims");
  }

  /**
   * Remove orphaned promo items from cart.
   *
   * An item is considered orphaned when ANY of these are true:
   * 1. Its _bxgy_config_id doesn't match any active promotion config
   *    (promotion was removed, deactivated, or already filtered as expired)
   * 2. The promotion's qualifying "buys" items are no longer in the cart
   *    (e.g. customer removed the product that triggered the promo)
   *
   * @param {Object} cart - Current cart data
   * @returns {Promise<boolean>} True if items were removed
   */
  async removeOrphanedPromoItems(cart) {
    if (!cart?.items || cart.items.length === 0) {
      return false;
    }

    // Build a map of active configs keyed by ID for quick lookup
    const activeConfigs = new Map(
      (this.configs?.configurations || [])
        .filter((config) => config.active && config.status === "ACTIVE")
        .map((config) => [config.id, config]),
    );

    // Find cart items that are orphaned
    const orphanedItems = cart.items.filter((item) => {
      const configId = item.properties?._bxgy_config_id;
      if (!configId) return false;

      // Case 1: config no longer active (removed, deactivated, or expired)
      const config = activeConfigs.get(configId);
      if (!config) {
        return true;
      }

      // Case 2: qualifying "buys" items are no longer in the cart
      const hasBuys = this.hasQualifyingBuysInCart(config, cart);
      if (!hasBuys) {
        return true;
      }
      return false;
    });

    if (orphanedItems.length === 0) {
      return false;
    }

    // Build updates to remove orphaned items (set quantity to 0)
    const updates = orphanedItems.map((item) => ({
      id: item.key,
      quantity: 0,
    }));

    // If removing orphaned items would leave only gift wrap in the cart, include
    // gift wrap in the same batch. Sending them separately would trigger the
    // server-side "gift wrap cannot be purchased alone" (422) validation error.
    const orphanedKeys = new Set(orphanedItems.map((item) => item.key));
    const remainingItems = cart.items.filter(
      (item) => !orphanedKeys.has(item.key),
    );
    const isGiftWrapItem = (item) => item.properties?._is_gift_wrap === "true";
    const onlyGiftWrapRemains =
      remainingItems.length > 0 && remainingItems.every(isGiftWrapItem);

    if (onlyGiftWrapRemains) {
      this.log(
        "Including gift wrap in orphan removal to avoid 'gift wrap alone' validation error",
      );
      for (const item of remainingItems) {
        updates.push({ id: item.key, quantity: 0 });
      }
    }

    await this.cartOperations.batchUpdateCart(updates, {
      name: "orphaned-cleanup",
    });

    return true;
  }

  /**
   * Check whether the cart still contains qualifying "buys" items for a promotion.
   *
   * For "all_products" promotions, any non-promo, non-gift-card item qualifies.
   * For variant-specific promotions, at least one matching variant must be present.
   *
   * @param {Object} config - Promotion configuration
   * @param {Object} cart - Current cart data
   * @returns {boolean} True if qualifying items exist
   */
  hasQualifyingBuysInCart(config, cart) {
    const { customerBuys } = config;

    if (customerBuys.anyItemsFrom === "all_products") {
      // Any non-promo, non-gift-card item qualifies
      const qualifying = cart.items.filter(
        (item) =>
          !this.promotionProcessor.isPromotionalItem(item) && !item.gift_card,
      );
      console.log(
        `[BXGY:buys] "${config.name}" (all_products) — ${qualifying.length} qualifying non-promo item(s):`,
        qualifying.map((item) => ({
          title: item.title,
          variant_id: item.variant_id,
          qty: item.quantity,
          is_promo: this.promotionProcessor.isPromotionalItem(item),
        })),
      );
      return qualifying.length > 0;
    }

    // Check for specific variant IDs
    const eligibleItems = this.promotionProcessor
      .getEligibleCartItems(cart, customerBuys.variantIds)
      .filter((item) => !this.promotionProcessor.isPromotionalItem(item));

    console.log(
      `[BXGY:buys] "${config.name}" (specific variants) — looking for variantIds: [${customerBuys.variantIds}], found ${eligibleItems.length} qualifying item(s):`,
      eligibleItems.map((item) => ({
        title: item.title,
        variant_id: item.variant_id,
        qty: item.quantity,
      })),
    );
    return eligibleItems.length > 0;
  }

  /**
   * Check all promotions for excess items and remove them
   * This handles the case where qualifying quantity decreased
   * (e.g., customer had 2 pencils → 2 promos triggered, now has 1 pencil → should only have 1 promo item)
   *
   * NOTE: With stacking support enabled, multiple promotions can use the same
   * qualifying items independently. Each promotion is validated against the
   * full cart quantity, not reduced by other promotions' claims.
   */
  async checkAndRemoveExcessItems(cart) {
    if (!this.configs?.configurations) return;

    this.log(`\n🔍 Checking for excess items across all promotions...`);

    // Reset claims for this evaluation - we need to re-calculate from scratch
    // With stacking support, each promo can claim independently
    this.claimsTracker.reset();

    // Get ALL active promotions sorted by priority (same order as when adding)
    const activeConfigs = this.configs.configurations
      .filter((config) => config.active && config.status === "ACTIVE")
      .sort((a, b) => {
        const priorityA =
          a.shopPromotionSettings?.priority ?? a.priority ?? 999;
        const priorityB =
          b.shopPromotionSettings?.priority ?? b.priority ?? 999;
        return priorityA - priorityB;
      });

    // First pass: determine which promos actually get claims (in priority order)
    const promoClaimResults = new Map();

    for (const config of activeConfigs) {
      // Get eligible items for this promo's customerBuys
      let eligibleItems = [];
      if (config.customerBuys.anyItemsFrom === "all_products") {
        eligibleItems = cart.items.filter(
          (item) =>
            !this.promotionProcessor.isPromotionalItem(item) && !item.gift_card,
        );
      } else {
        eligibleItems = this.promotionProcessor
          .getEligibleCartItems(cart, config.customerBuys.variantIds)
          .filter((item) => !this.promotionProcessor.isPromotionalItem(item));
      }

      if (eligibleItems.length === 0) {
        promoClaimResults.set(config.id, { qualifiedUses: 0, canClaim: false });
        continue;
      }

      // Attempt to claim using the tracker (respects already-claimed quantities)
      const claimResult = this.claimsTracker.attemptClaim(
        config.id,
        eligibleItems.map((item) => ({
          key: item.key,
          quantity: item.quantity,
          final_price: item.final_price,
        })),
        {
          type:
            config.customerBuys.requirementType === "minimum_quantity"
              ? "quantity"
              : "amount",
          value:
            config.customerBuys.requirementType === "minimum_purchase_amount"
              ? config.customerBuys.minimumAmount
              : config.customerBuys.minimumQuantity,
        },
      );

      if (claimResult.success) {
        // Cap by usesPerOrderLimit
        const actualUses = Math.min(
          claimResult.qualifiedUses,
          config.usesPerOrderLimit || 1,
        );
        const minQty = config.customerBuys?.minimumQuantity || 1;
        const totalQtyToClaim = actualUses * minQty;

        // Commit limited claims
        const limitedClaims = this.limitClaims(
          claimResult.claims,
          totalQtyToClaim,
        );
        const requirementType =
          config.customerBuys?.requirementType === "minimum_purchase_amount"
            ? "amount"
            : "quantity";
        const committed = this.claimsTracker.commitClaims(
          config.id,
          limitedClaims,
          {
            requirementType,
          },
        );

        promoClaimResults.set(config.id, {
          qualifiedUses: actualUses,
          canClaim: true,
        });

        if (committed) {
          this.log(
            `📊 ${config.name}: claimed ${totalQtyToClaim} qty, qualified for ${actualUses} uses`,
          );
        } else {
          this.log(`📊 ${config.name}: qualified for ${actualUses} uses`);
        }
      } else {
        promoClaimResults.set(config.id, { qualifiedUses: 0, canClaim: false });
        this.log(
          `📊 ${config.name}: cannot claim (insufficient quantity/amount)`,
        );
      }
    }

    // Second pass: check each promo's items against what it can actually claim
    for (const config of activeConfigs) {
      const currentPromoCount = this.promotionProcessor.getGetItemsCountInCart(
        config,
        cart,
      );

      if (currentPromoCount === 0) {
        const claimInfo = promoClaimResults.get(config.id) || {
          qualifiedUses: 0,
        };
        if (
          claimInfo.qualifiedUses === 0 &&
          this.processedPromotions.has(config.id)
        ) {
          this.processedPromotions.delete(config.id);
          this.log(
            `Removed "${config.name}" from processed (no longer qualifies, no promo items in cart)`,
          );
        }
        continue;
      }

      const claimInfo = promoClaimResults.get(config.id) || {
        qualifiedUses: 0,
      };

      // Calculate max allowed promo items based on CLAIMED uses (not raw qty)
      const rewardStrategy = config.customerGets?.rewardStrategy || "all";
      const getsQuantity = config.customerGets?.quantity || 1;
      const variantCount = config.customerGets?.variantIds?.length || 0;

      let maxAllowedItems;
      if (rewardStrategy === "choice") {
        maxAllowedItems = getsQuantity * claimInfo.qualifiedUses;
      } else {
        maxAllowedItems = getsQuantity * variantCount * claimInfo.qualifiedUses;
      }

      const excessCount = currentPromoCount - maxAllowedItems;

      this.log(
        `📋 ${config.name}: ${currentPromoCount} items in cart, ${claimInfo.qualifiedUses} qualified uses, max allowed ${maxAllowedItems}`,
      );

      if (excessCount > 0) {
        this.log(
          `🗑️ Excess items for "${config.name}": removing ${excessCount}`,
        );

        await this.cartOperations.removeExcessPromotionItems(
          config,
          cart,
          excessCount,
          this.promotionProcessor.isItemFromPromotion.bind(
            this.promotionProcessor,
          ),
        );

        // Update processedPromotions status
        if (maxAllowedItems === 0) {
          this.processedPromotions.delete(config.id);
          this.log(
            `Removed "${config.name}" from processed (no longer qualifies)`,
          );
        }
      } else if (currentPromoCount > 0 && maxAllowedItems > currentPromoCount) {
        // Has promo items but could have more - remove from processed
        if (this.processedPromotions.has(config.id)) {
          this.processedPromotions.delete(config.id);
          this.log(
            `Removed "${config.name}" from processed (can add more items)`,
          );
        }
      }
    }
  }

  /**
   * Limit claims to only the needed quantity
   * Distributes the limit across multiple line claims
   *
   * @param {Array<{lineKey: string, quantity: number}>} claims - Original claims
   * @param {number} totalQtyToKeep - Total quantity to keep across all claims
   * @returns {Array<{lineKey: string, quantity: number}>} Limited claims
   */
  limitClaims(claims, totalQtyToKeep) {
    const limitedClaims = [];
    let remaining = totalQtyToKeep;

    for (const claim of claims) {
      if (remaining <= 0) break;

      const qtyFromThisClaim = Math.min(claim.quantity, remaining);
      if (qtyFromThisClaim > 0) {
        limitedClaims.push({
          lineKey: claim.lineKey,
          quantity: qtyFromThisClaim,
        });
        remaining -= qtyFromThisClaim;
      }
    }

    return limitedClaims;
  }

  log(...args) {
    if (this.debugMode) {
      console.log("[BXGY]", ...args);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    window.bxgyController = new BXGYController();
  });
} else {
  window.bxgyController = new BXGYController();
}

export default BXGYController;
